import {
    OnChanges,
    OnInit,
    OnDestroy,
    SimpleChanges,
    AfterViewInit,
    ElementRef,
    Renderer2,
    Input,
    Output, EventEmitter, ViewChild, HostListener, AfterViewChecked
} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import * as moment from 'moment';
import * as fileSaver from 'file-saver';
import 'rxjs/add/operator/first';

import {BufferGraphService} from 'app/buffer-graph/services/buffer-graph.service';
import {SlickGridComponent} from 'app/slick-grid/slick-grid.component';
import {ISlickGridColumn} from 'app/slick-grid/interfaces/slick-grid-column';
import {Formatters} from "../../slick-grid/models/formatters";
import {Aggregation, AggregationSelector} from "../../stocks/models/aggregation-selector";
import {ColumnSettingsBase} from "../../slick-grid/models/column-settings-base";
import {TranslateService} from "@ngx-translate/core";
import {ChartHelper} from "app/buffer-graph/models/chart-helper";
import {CodesSelectorItem} from "../../codes-selector/models/codes-selector-item";
import {ISubscription} from "rxjs/Subscription";
import {BufferGraphDataExchangeService} from "../services/buffer-graph-data-exchange.service";
import {ITabInfo} from "./tab-info-interface";
import {ChartData} from "./chart-data";
import {PeriodSelectorModel} from "../../shared/models/period-selector-model";
import {CodeName} from "../../stocks/models/code-name";
import {ColumnsGenerator} from "../../slick-grid/models/columns-generator";
import {OnDropdownFilteredEventArgs} from "../../slick-grid/models/on-dropdown-filtered-event-args";
import {TabInfoBase} from "./tab-info-base";

export abstract class TabComponentBase<T extends Slick.SlickData, T2 extends { columns: { header: string, field: string }[] }> implements OnChanges, OnInit, AfterViewInit, OnDestroy, AfterViewChecked{
    protected constructor(protected readonly graphService: BufferGraphService,
                          protected readonly exchangeService: BufferGraphDataExchangeService,
                          protected readonly translateService: TranslateService,
                          protected readonly renderer: Renderer2,
                          protected readonly elementRef: ElementRef
    ){
        this.columns = [];
        this.lineColumns = [];
        this.helper = new ChartHelper();
        this.updateState = new EventEmitter<string>();
        this.updateData = new EventEmitter<PeriodSelectorModel>();
        this.close = new EventEmitter<ITabInfo>();
        this.toggleChart = new EventEmitter<void>();
        this.mainGridCssStyles = {};
        this.mainGridColumnList = [];
        this.selectionModel = new Slick.CellSelectionModel<CodeName, Slick.Range[]>();

    }

    protected abstract gridRef: SlickGridComponent;

    @ViewChild('mainWrp')
    protected mainWrp: ElementRef;

    @ViewChild('periodSelectorBtn')
    protected periodSelectorBtn: ElementRef;

    @ViewChild('tabHeaderWrp')
    protected tabHeaderWrp: ElementRef;

    protected gridLineRef: SlickGridComponent;

    // protected abstract sku: string;
    //
    // protected abstract warehouseCode: string;

    protected abstract canSend: boolean;

    protected abstract isActive: boolean;

    protected addCustomFormatter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    protected addSourceFilter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    protected addTotalsFormatter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    protected generateLineColumns: (columns: ISlickGridColumn<T>[], columnsSettings: ColumnSettingsBase[], gridRef: SlickGridComponent) => void;

    protected removeSort: () => void;

    protected resizeChartsLegendsView: boolean;

    protected readonly alertTime = 10000;

    protected dataView: Slick.Data.DataView<T>;

    protected columns: ISlickGridColumn<T>[];

    protected lineColumns: ISlickGridColumn<any>[];

    protected options: Slick.GridOptions<T>;

    protected showExportError: boolean;

    protected showExportErrorTimer: any;

    protected showSendError: boolean;

    protected showSendErrorTimer: any;

    protected showSendSuccess: boolean;

    protected showSendSuccessTimer: any;

    protected sending: boolean;

    protected exporting: boolean;

    protected destroyed: boolean;

    protected popperUpdate: boolean;

    protected showPeriodSelector: boolean;

    protected styleGridCells: boolean;

    protected mainGridCssStyles: { [key: string]: Slick.CellCssStylesHash };

    protected mainGridColumnList: string[];

    private subscriptionResizeGrid: ISubscription;

    @Input()
    protected columnSettings?: ColumnSettingsBase[];

    @Input()
    protected columnLineSettings?: ColumnSettingsBase[];

    @Input()
    protected tab: TabInfoBase<any>;

    @Input()
    protected chartData: ChartData;

    @Input()
    protected sharedGUID: string;

    @Input()
    protected chartType: string;

    @Input()
    protected isAdmin: boolean;

    @Output()
    protected updateState: EventEmitter<string>;

    @Output()
    protected close: EventEmitter<ITabInfo>;

    @Output()
    protected updateData: EventEmitter<PeriodSelectorModel>;

    @Output()
    protected toggleChart: EventEmitter<void>;

    protected generateColumns: boolean;

    protected helper: ChartHelper;

    protected fullWidthTab: boolean;

    protected mainRowsSheetName: string;

    protected treeRowsSheetName: string;

    protected lineRowsSheetName: string;

    protected resizeGrids: boolean;

    protected updateColumns: boolean;

    public selectionModel: Slick.CellSelectionModel<any, Slick.Range[]>;

    @HostListener('window:resize')
    private onResize = (): void => {
        this.helper.wait(() => this.onTabsResized(this.sharedGUID), 1);
    }


    ngOnChanges(changes: SimpleChanges): void{
        if (!changes['isActive'].currentValue)
            return;
        this.helper.wait(() => this.onTabsResized(this.sharedGUID), 1);
        this.updateColumns = true;
    }

    ngOnInit(): void{
        // this.graphService.onTabsResized.takeWhile(() => !this.destroyed).subscribe(() => this.onTabsResized());
        //# recalculate tab dimensions, resize grids
        this.subscriptionResizeGrid = this.exchangeService.tabsResizeCurrent.subscribe(sharedGUID => this.helper.tryCatch(() =>this.onTabsResized(sharedGUID)));
    }

    async ngAfterViewInit(): Promise<void>{
        //this.onTabsResized(this.sharedGUID);
        if (this.generateColumns){
            await this.columnsGenerate(this.columns, this.columnSettings, this.gridRef);
            this.columnLineSettings && this.columnLineSettings.length ? await this.columnsGenerate(this.lineColumns, this.columnLineSettings, this.gridLineRef) : null;
        }
        this.helper.wait(() => this.onTabsResized(this.sharedGUID), 1);
        this.helper.wait(() =>
                $(`[data-toggle="tooltip"]`).tooltip({trigger: 'hover', offset: "50px, 0", template: '<div class="tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div></div>'})
            , 500);
        if (this.tab.gridFilters && this.tab.gridFilters.length){
            this.restoreFilters();
        }
        if (this.tab.sorting){
            this.restoreSorting();
        }

    }

    ngAfterViewChecked(): void{
            if (this.resizeGrids){
                this.resizeGrids = false;

                if (this.gridRef){
                    this.gridRef.resizeGrid();
                }

                if (this.gridLineRef){
                    this.gridLineRef.resizeGrid();
                }
            }

        if (this.updateColumns){
            this.updateColumns = false;

            if (this.gridRef){
                this.gridRef.setColumns(this.columns);
            }

            if (this.gridLineRef){
                this.gridLineRef.setColumns(this.lineColumns);
            }
        }
    }

    ngOnDestroy(): void{
        this.destroyed = true;
        this.subscriptionResizeGrid.unsubscribe();
    }

    //# set cell styles to main grid
    protected setMainGridSellStyles = (): void => {
        this.mainGridCssStyles = this.helper.setCellStyles(this.dataView);
    }

    //# recalculate tab dimensions, resize grids
    protected onTabsResized(sharedGUID: string): void{
        if (this.sharedGUID !== sharedGUID){
            return;
        }

        this.resizeGrids = true;
        // this.updateColumns = true;
        this.popperUpdate = true;
        this.resizeChartsLegendsView = true;
        // console.log(this.tab, 'this.tab')
        // console.log(this.mainWrp, 'this.mainWrp')
        // console.log(this.tabHeader, 'this.tabHeader')


        if (this.tab.viewMode){
            try {
                // console.log(this.mainWrp.nativeElement, 'this.mainWrp')
                // console.log(this.tabHeaderWrp.nativeElement, 'this.tabHeader')
                // console.log(this.tabHeaderWrp, 'this.tabHeader')
                // console.log(this.tab.viewMode, 'this.tab.viewMode')
                this.fullWidthTab = false;
                // this.helper.wait(() => this.fullWidthTab = false);
            } catch (e){

            }



            if (this.mainWrp && this.mainWrp.nativeElement && this.tabHeaderWrp && this.tabHeaderWrp.nativeElement){
                this.renderer.setStyle(this.tabHeaderWrp.nativeElement, 'width', 'auto');
                this.helper.wait(() =>{
                    let normalWidth = this.mainWrp.nativeElement.parentElement.offsetWidth - 25,
                        headerWidth = this.tabHeaderWrp.nativeElement.offsetWidth;
                    // console.log(normalWidth, headerWidth, 'normalWidth, headerWidth')
                    if (normalWidth < headerWidth){
                        // console.log('normalWidth < headerWidth')
                        this.helper.wait(() =>{
                            this.fullWidthTab = true;
                            // this.tab.id === 'IA' ? this.renderer.setStyle(this.tabHeaderWrp.nativeElement, 'width', (normalWidth) + 'px') : null;
                        })
                        // console.log(this.tabHeaderWrp.nativeElement, 'this.tabHeader')
                    } else{
                        this.helper.wait(() => this.fullWidthTab = false);
                    }
                }, 10)
            }
        } else{

        }


    }

    protected removeTab = (tab: ITabInfo): void =>{
        this.close.emit(tab);
    }

    //# sens excel to email function
    protected sendToEmail = (makeRequest: (data: T2) => Observable<{ success: boolean }>): void =>{
        this.export(true, makeRequest, null);
    }

    //# download excel to email function
    protected download = (makeRequest: (data: T2) => Observable<{ fileContent: Blob, fileName: string }>): void =>{
        this.export(false, null, makeRequest);
    }

    //# generate columns for grids in tab
    protected async columnsGenerate(columns: ISlickGridColumn<T>[], columnsSettings: ColumnSettingsBase[], gridRef: SlickGridComponent): Promise<void>{

        let generator = new ColumnsGenerator(this.translateService, this.renderer, this.elementRef);
        //# add custom filters or formatters to grid
        generator.addCustomFormatter = this.addCustomFormatter;
        generator.addSourceFilter = this.addSourceFilter;
        generator.addTotalsFormatter = this.addTotalsFormatter;

        await generator.columnsGenerate(columns, columnsSettings, this.isAdmin);

        if (gridRef){
            gridRef.setColumns(columns);
        } else{
            this.helper.wait(() =>{
                if (gridRef)
                    gridRef.setColumns(columns);
            }, 100)
        }

        console.log(columns, 'columns')
    }

    protected abstract getExportData: () => T2;

    //# save to row data from grid row
    protected saveRow = (row: any, val: any, prop: string, convRow: any): void =>{
        if (moment.isMoment(val)){
            convRow[prop] = val.toJSON();
        } else if (moment.isDuration(val)){
            convRow[prop] = moment(val.asMilliseconds()).toJSON();
        } else{
            convRow[prop] = `${val == null ? '' : val}`;
        }
    };

    //# get export data fot default table (one grid no tree structure)
    protected getExportDataByDefault = (): { columns: { header: string, field: string }[], mainRows: any[] } =>{
        const columns = this.gridRef.getColumns().map(c =>{
            return {header: c.name, field: c.field};
        });
        let mainRows = [];

        const length = this.dataView.getLength();
        for (let i = 0; i < length; i++){
            const data = this.dataView.getItem(i);

            const row = {};
            columns.forEach(c =>{
                this.saveRow(data, data[c.field], c.field, row);
            });
            mainRows.push(row);
        }

        !mainRows.length ? mainRows = null : null;

        return {columns, mainRows};
    }

    //# export data to file or send file to email
    private export = (toEmail: boolean, emailRequest: (data: T2) => Observable<{ success: boolean }>, downloadRequest: (data: T2) => Observable<{ fileContent: Blob, fileName: string }>): void =>{
        if (toEmail)
            this.sending = true;
        else
            this.exporting = true;

        const showError = (): void =>{
            if (toEmail){
                this.showExportError = false;
                this.showSendSuccess = false;

                if (this.showSendErrorTimer)
                    clearTimeout(this.showSendErrorTimer);

                this.showSendError = true;
                setTimeout(() =>{
                        this.showSendError = false;
                        clearTimeout(this.showSendErrorTimer);
                    },
                    this.alertTime);
                return;
            }

            this.showSendError = false;
            this.showSendSuccess = false;

            if (this.showExportErrorTimer)
                clearTimeout(this.showExportErrorTimer);

            this.showExportError = true;
            setTimeout(() =>{
                    this.showExportError = false;
                    clearTimeout(this.showExportErrorTimer);
                },
                this.alertTime);
        };

        if (toEmail){
            emailRequest(this.getExportData()).first().subscribe((data: { success: boolean }) =>{
                this.sending = false;

                if (data && data.success){
                    if (this.showSendSuccess)
                        clearTimeout(this.showSendSuccessTimer);

                    this.showExportError = false;
                    this.showSendError = false;
                    this.showSendSuccess = true;
                    setTimeout(() =>{
                            this.showSendSuccess = false;
                            clearTimeout(this.showSendSuccessTimer);
                        },
                        this.alertTime);
                    return;
                }

                showError();
            }, () =>{
                this.sending = false;
                showError();
            });
            return;
        }

        downloadRequest(this.getExportData()).first().subscribe((data: { fileContent: Blob, fileName: string }) =>{
                this.exporting = false;
                if (data && data.fileContent && data.fileContent.size > 0){
                    fileSaver.saveAs(data.fileContent, data.fileName);
                    return;
                }

                showError();
            },
            () =>{
                this.exporting = false;
                showError();
            });
    }

    //# toggle period selector visibility, not used now
    protected onTogglePeriodSelector = (): void => {
        this.showPeriodSelector = !this.showPeriodSelector;
    }

    //# hide period selector, emit selected period, not used now
    protected onShowPeriodClick = (dates: PeriodSelectorModel): void => {
        dates ? this.updateData.emit(dates) : null;
        this.showPeriodSelector = false;
    }

    //# hide messages with delay
    protected onHideExportErrorClick = (): void =>{
        setTimeout(() => this.showExportError = false, 500);
    }

    protected onHideSendErrorClick = (): void =>{
        setTimeout(() => this.showSendError = false, 500);
    }

    protected onHideSendSuccessClick = (): void =>{
        setTimeout(() => this.showSendSuccess = false, 500);
    }

    public saveFilters = (data: {filteredColumns: string[], filters: OnDropdownFilteredEventArgs<any>[]}): void => {
        this.tab.gridFilters = data.filters.map(i => i);
    }

    public restoreFilters = (): void => {
        this.gridRef.addFilters(this.tab.gridFilters.map(item => item.column.field), this.tab.gridFilters)
    }

    public saveSorting = (args: {event: Slick.EventData | DOMEvent, data: Slick.OnSortEventArgs<any>}): void => {
        this.tab.sorting = args
            ? {columnId: args.data.sortCols[0].sortCol.id, asc: args.data.sortCols[0].sortAsc}
            : null;
        this.removeSort && this.removeSort();
    }

    public restoreSorting = (): void => {
        let data = this.dataView.getItems();
        if (data && data.length){

            let columnId = this.tab.sorting.columnId,
                asc = this.tab.sorting.asc;

            if (typeof data[0][columnId] === "number"){
                data.sort((a: any, b: any) =>{
                    return asc ? a[columnId] - b[columnId] : b[columnId] - a[columnId];
                });
            } else if (typeof data[0][columnId] === "string"){
                data.sort((a, b) =>{
                    return ('' + a[columnId]).localeCompare(b[columnId]);
                });

                if (!asc){
                    data = data.reverse();
                }
            }
            this.dataView.setItems(data);
            this.gridRef.setSortColumn(columnId, asc);
            this.gridRef.invalidate();
        }

    }
}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/models/tab-component-base.ts